import React, { Component } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // You can log the error to an error reporting service
    // console.error(`Error caught by ErrorBoundary${this.props.componentName ? ` in ${this.props.componentName}` : ''}:`, error, errorInfo);
    this.setState({ errorInfo });
    
    // Optional: Log to your analytics or error tracking service
    // logErrorToService(error, errorInfo);
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  }

  render() {
    if (this.state.hasError) {
      // If this is a sidebar error, use simple UI
      if (this.props.componentName === 'Sidebar') {
        return (
          <div className="p-4 text-center">
            <h1 className="text-red-500 text-sm font-medium mb-2">Something went wrong with the sidebar.</h1>
            <button 
              onClick={this.handleReset}
              className="text-xs bg-gray-200 px-2 py-1 rounded hover:bg-gray-300"
            >
              Try Again
            </button>
          </div>
        );
      }
      
      // Default: Customized fallback UI for general errors
      return (
        <div className="min-h-[300px] flex flex-col items-center justify-center p-6 bg-white rounded-2xl shadow-sm border border-red-100">
          <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mb-4">
            <AlertTriangle className="h-8 w-8 text-red-500" />
          </div>
          
          <h2 className="text-xl font-bold text-gray-800 mb-2">Oops! Something went wrong</h2>
          
          <p className="text-gray-600 text-center max-w-md mb-6">
            We're having trouble displaying {this.props.componentName ? `the ${this.props.componentName}` : 'this part of the application'}. 
            This doesn't affect your other work.
          </p>
          
          <button
            onClick={this.handleReset}
            className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-full hover:bg-indigo-700 transition-colors"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Try Again
          </button>
          
          {this.props.showDetails && (
            <details className="mt-4 text-xs text-gray-500 max-w-md">
              <summary className="cursor-pointer">Technical Details</summary>
              <pre className="mt-2 p-2 bg-gray-50 rounded overflow-auto">
                {this.state.error?.toString()}
                {this.state.errorInfo?.componentStack}
              </pre>
            </details>
          )}
        </div>
      );
    }

    // If there's no error, render children normally
    return this.props.children;
  }
}

export default ErrorBoundary; 